/**
 * Material Design 3 Typography System
 *
 * MD3 type scale mapped to MUI typography variants.
 * Uses Roboto/Google Sans with regular/medium weights and normal letter spacing.
 *
 * @see https://m3.material.io/styles/typography
 */

// ============================================================================
// Font Families
// ============================================================================

export const materialFontFamilies = {
  primary: [
    '"Roboto"',
    '"Google Sans"',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(', '),

  mono: [
    '"Roboto Mono"',
    'ui-monospace',
    'SFMono-Regular',
    'Menlo',
    'Monaco',
    'Consolas',
    '"Liberation Mono"',
    '"Courier New"',
    'monospace',
  ].join(', '),

  display: ['"Google Sans"', '"Roboto"', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(', '),
} as const;

// ============================================================================
// Font Weights (MD3)
// ============================================================================
// MD3 primarily uses Regular (400) and Medium (500).

export const materialFontWeights = {
  regular: 400,
  medium: 500,
} as const;

// ============================================================================
// MUI Typography Variants (MD3 Type Scale)
// ============================================================================
// Mapping MD3 type scale roles to MUI variants:
//   Display Large  → h1
//   Headline Large → h2
//   Headline Medium → h3
//   Title Large    → h4
//   Title Medium   → h5
//   Title Small    → h6
//   Body Large     → body1
//   Body Medium    → body2
//   Label Large    → button
//   Body Small     → caption
//   Label Small    → overline

export const materialTypographyVariants = {
  fontFamily: materialFontFamilies.primary,
  fontSize: 14, // MD3 base font size

  // Display Large — hero sections, large display text
  h1: {
    fontFamily: materialFontFamilies.display,
    fontWeight: materialFontWeights.regular,
    fontSize: '3.5625rem', // 57px
    lineHeight: 1.12, // 64px / 57px
    letterSpacing: '-0.015625em', // -0.25px
  },

  // Headline Large — page titles
  h2: {
    fontFamily: materialFontFamilies.display,
    fontWeight: materialFontWeights.regular,
    fontSize: '2rem', // 32px
    lineHeight: 1.25, // 40px / 32px
    letterSpacing: '0',
  },

  // Headline Medium — section titles
  h3: {
    fontFamily: materialFontFamilies.display,
    fontWeight: materialFontWeights.regular,
    fontSize: '1.75rem', // 28px
    lineHeight: 1.2857, // 36px / 28px
    letterSpacing: '0',
  },

  // Title Large — card headers, dialog titles
  h4: {
    fontFamily: materialFontFamilies.display,
    fontWeight: materialFontWeights.regular,
    fontSize: '1.375rem', // 22px
    lineHeight: 1.2727, // 28px / 22px
    letterSpacing: '0',
  },

  // Title Medium — list headers, section labels
  h5: {
    fontFamily: materialFontFamilies.primary,
    fontWeight: materialFontWeights.medium,
    fontSize: '1rem', // 16px
    lineHeight: 1.5, // 24px / 16px
    letterSpacing: '0.009375em', // 0.15px
  },

  // Title Small — tabs, compact headers
  h6: {
    fontFamily: materialFontFamilies.primary,
    fontWeight: materialFontWeights.medium,
    fontSize: '0.875rem', // 14px
    lineHeight: 1.4286, // 20px / 14px
    letterSpacing: '0.00625em', // 0.1px
  },

  // Subtitle variants map to MD3 Title roles
  subtitle1: {
    fontWeight: materialFontWeights.medium,
    fontSize: '1rem', // 16px
    lineHeight: 1.5,
    letterSpacing: '0.009375em',
  },

  subtitle2: {
    fontWeight: materialFontWeights.medium,
    fontSize: '0.875rem', // 14px
    lineHeight: 1.4286,
    letterSpacing: '0.00625em',
  },

  // Body Large — primary body text
  body1: {
    fontWeight: materialFontWeights.regular,
    fontSize: '1rem', // 16px
    lineHeight: 1.5, // 24px / 16px
    letterSpacing: '0.03125em', // 0.5px
  },

  // Body Medium — secondary body text
  body2: {
    fontWeight: materialFontWeights.regular,
    fontSize: '0.875rem', // 14px
    lineHeight: 1.4286, // 20px / 14px
    letterSpacing: '0.015625em', // 0.25px
  },

  // Label Large — buttons, prominent labels
  button: {
    fontWeight: materialFontWeights.medium,
    fontSize: '0.875rem', // 14px
    lineHeight: 1.4286, // 20px / 14px
    letterSpacing: '0.00625em', // 0.1px
    textTransform: 'none' as const,
  },

  // Body Small — captions, timestamps
  caption: {
    fontWeight: materialFontWeights.regular,
    fontSize: '0.75rem', // 12px
    lineHeight: 1.3333, // 16px / 12px
    letterSpacing: '0.025em', // 0.4px
  },

  // Label Small — overlines, tiny labels
  overline: {
    fontWeight: materialFontWeights.medium,
    fontSize: '0.6875rem', // 11px
    lineHeight: 1.4545, // 16px / 11px
    letterSpacing: '0.03125em', // 0.5px
    textTransform: 'none' as const,
  },
} as const;

// ============================================================================
// Responsive Typography (MD3)
// ============================================================================
// MD3 recommends scaling Display and Headline down on smaller viewports.

export const materialResponsiveTypography = {
  h1: {
    '@media (max-width: 599px)': {
      fontSize: '2.25rem', // 36px — scaled down from 57px
    },
    '@media (min-width: 600px) and (max-width: 900px)': {
      fontSize: '2.8125rem', // 45px
    },
  },
  h2: {
    '@media (max-width: 599px)': {
      fontSize: '1.5rem', // 24px — scaled down from 32px
    },
    '@media (min-width: 600px) and (max-width: 900px)': {
      fontSize: '1.75rem', // 28px
    },
  },
  h3: {
    '@media (max-width: 599px)': {
      fontSize: '1.375rem', // 22px — scaled down from 28px
    },
  },
} as const;
